import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Tooltip from "@material-ui/core/Tooltip";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import AddIcon from "@material-ui/icons/Add";
import AttachFileIcon from "@material-ui/icons/AttachFile";

import MainContainer from "../../components/MainContainer";
import Title from "../../components/Title";
import listPageStyles from "../../styles/listPageStyles";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import QuickMessageDialog from "../../components/QuickMessageDialog";
import ConfirmationModal from "../../components/ConfirmationModal";
import MobileRecordCard, { MobileRecordCardSkeleton } from "../../components/MobileRecordCard";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import { AuthContext } from "../../context/Auth/AuthContext";

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------
const reducer = (state, action) => {
  if (action.type === "LOAD_QUICKMESSAGES") {
    const quickmessages = action.payload;
    const newItems = [];
    if (Array.isArray(quickmessages)) {
      quickmessages.forEach((qm) => {
        const idx = state.findIndex((u) => u.id === qm.id);
        if (idx !== -1) state[idx] = qm;
        else newItems.push(qm);
      });
    }
    return [...state, ...newItems];
  }
  if (action.type === "UPDATE_QUICKMESSAGES") {
    const qm = action.payload;
    const idx = state.findIndex((u) => u.id === qm.id);
    if (idx !== -1) { state[idx] = qm; return [...state]; }
    return [qm, ...state];
  }
  if (action.type === "DELETE_QUICKMESSAGE") {
    const id = action.payload;
    const idx = state.findIndex((u) => u.id === id);
    if (idx !== -1) state.splice(idx, 1);
    return [...state];
  }
  if (action.type === "RESET") return [];
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const useStyles = makeStyles((theme) => ({
  ...listPageStyles(theme),
  shortcode: {
    display: "inline-flex",
    alignItems: "center",
    height: 26,
    padding: theme.spacing(0, 1),
    borderRadius: 6,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
    fontSize: 13,
    fontWeight: 600,
    whiteSpace: "nowrap",
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  },
  messageText: {
    fontSize: 14,
    lineHeight: "20px",
    color: theme.palette.text.secondary,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    maxWidth: 640,
  },
  messageCardText: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    display: "-webkit-box",
    WebkitLineClamp: 4,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  attachment: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 13,
    color: theme.palette.text.primary,
    maxWidth: 220,
    "& span": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  },
  mobileList: {
    padding: theme.spacing(1),
  },
}));

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const QuickMessages = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(null);
  const [selectedQuickMessage, setSelectedQuickMessage] = useState(null);
  const [deletingQuickMessage, setDeletingQuickMessage] = useState(null);
  const [quickMessageModalOpen, setQuickMessageModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [quickMessages, dispatch] = useReducer(reducer, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchQuickMessages();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = user.companyId;
    const socket = socketConnection({ companyId, userId: user.id });

    socket.on(`company-${companyId}-quickmessage`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_QUICKMESSAGES", payload: data.record });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_QUICKMESSAGE", payload: +data.id });
      }
    });
    return () => socket.disconnect();
  }, []);

  const fetchQuickMessages = async () => {
    try {
      const { data } = await api.get("/quick-messages", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_QUICKMESSAGES", payload: data.records });
      setHasMore(data.hasMore);
      if (typeof data.count === "number") setTotalCount(data.count);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenModal = () => {
    setSelectedQuickMessage(null);
    setQuickMessageModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedQuickMessage(null);
    setQuickMessageModalOpen(false);
    fetchQuickMessages();
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEdit = (qm) => {
    setSelectedQuickMessage(qm);
    setQuickMessageModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/quick-messages/${id}`);
      toast.success(i18n.t("quickMessages.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingQuickMessage(null);
    setSearchParam("");
    setPageNumber(1);
    dispatch({ type: "RESET" });
    fetchQuickMessages();
  };

  const loadMore = () => setPageNumber((prev) => prev + 1);

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) loadMore();
  };

  const renderActions = (qm) => (
    <div className={classes.actions}>
      <Tooltip title="Editar">
        <IconButton size="small" className={classes.iconAction} onClick={() => handleEdit(qm)} aria-label="Editar">
          <EditOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Excluir">
        <IconButton
          size="small"
          className={classes.deleteAction}
          aria-label="Excluir"
          onClick={() => {
            setConfirmModalOpen(true);
            setDeletingQuickMessage(qm);
          }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </div>
  );

  const renderAttachment = (qm) =>
    qm.mediaName ? (
      <Tooltip title={qm.mediaName}>
        <span className={classes.attachment}>
          <AttachFileIcon fontSize="small" className={classes.muted} />
          <span>{qm.mediaName}</span>
        </span>
      </Tooltip>
    ) : (
      <span className={classes.muted}>—</span>
    );

  const countLabel =
    totalCount === null
      ? ""
      : searchParam
      ? `${totalCount} ${totalCount === 1 ? "resposta encontrada" : "respostas encontradas"}`
      : `${totalCount} ${totalCount === 1 ? "resposta cadastrada" : "respostas cadastradas"} · use / no chat para inserir`;

  const emptyState = (
    <Box className={classes.emptyState}>
      <FlashOnIcon className={classes.emptyIcon} />
      <Typography variant="body2">
        {searchParam
          ? `Nenhuma resposta encontrada para "${searchParam}"`
          : "Nenhuma resposta rápida cadastrada ainda."}
      </Typography>
      {!searchParam && (
        <Button
          color="primary"
          variant="outlined"
          className={`${classes.headerButton} ${classes.secondaryButton}`}
          startIcon={<AddIcon />}
          onClick={handleOpenModal}
        >
          Criar primeira resposta
        </Button>
      )}
    </Box>
  );

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingQuickMessage &&
          `${i18n.t("quickMessages.confirmationModal.deleteTitle")} /${deletingQuickMessage.shortcode}?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDelete(deletingQuickMessage.id)}
      >
        {i18n.t("quickMessages.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <QuickMessageDialog
        resetPagination={() => {
          setPageNumber(1);
          fetchQuickMessages();
        }}
        open={quickMessageModalOpen}
        onClose={handleCloseModal}
        aria-labelledby="form-dialog-title"
        quickemessageId={selectedQuickMessage && selectedQuickMessage.id}
      />

      <div className={classes.header}>
        <div className={classes.headerTitle}>
          <Title>{i18n.t("quickMessages.title")}</Title>
          {countLabel && (
            <Typography variant="body2" className={classes.muted}>
              {countLabel}
            </Typography>
          )}
        </div>
        <div className={classes.headerButtons}>
          <Button
            variant="contained"
            color="primary"
            disableElevation
            className={classes.headerButton}
            startIcon={<AddIcon />}
            onClick={handleOpenModal}
          >
            Nova resposta
          </Button>
        </div>
      </div>

      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.toolbar}>
          <TextField
            className={classes.searchField}
            placeholder="Buscar por atalho ou texto"
            type="search"
            variant="outlined"
            size="small"
            value={searchParam}
            onChange={handleSearch}
            inputProps={{ "aria-label": "Buscar respostas rápidas" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
            }}
          />
        </div>

        <div className={classes.tableWrapper} onScroll={handleScroll}>
          {!loading && quickMessages.length === 0 && emptyState}

          {isMobile ? (
            (quickMessages.length > 0 || loading) && (
              <div className={classes.mobileList}>
                {quickMessages.map((qm) => (
                  <MobileRecordCard
                    key={qm.id}
                    title={<span className={classes.shortcode}>/{qm.shortcode}</span>}
                    headerAction={renderActions(qm)}
                    body={
                      <Typography className={classes.messageCardText}>
                        {qm.message || "—"}
                      </Typography>
                    }
                    rows={[
                      {
                        label: "Anexo",
                        value: qm.mediaName || "—",
                      },
                    ]}
                  />
                ))}
                {loading && [0, 1, 2].map((i) => <MobileRecordCardSkeleton key={i} />)}
              </div>
            )
          ) : (
            (quickMessages.length > 0 || loading) && (
              <Table stickyHeader size="small" style={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.headCell} style={{ paddingLeft: 20, width: 220 }}>
                      Atalho
                    </TableCell>
                    <TableCell className={classes.headCell}>Resposta</TableCell>
                    <TableCell className={classes.headCell} style={{ width: 220 }}>Anexo</TableCell>
                    <TableCell className={classes.headCell} align="right" style={{ paddingRight: 20, width: 110 }}>
                      Ações
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {quickMessages.map((qm) => (
                    <TableRow key={qm.id} className={classes.row}>
                      <TableCell style={{ paddingLeft: 20 }}>
                        <span className={classes.shortcode}>/{qm.shortcode}</span>
                      </TableCell>
                      <TableCell>
                        <Typography className={classes.messageText}>
                          {qm.message || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>{renderAttachment(qm)}</TableCell>
                      <TableCell style={{ paddingRight: 20 }}>{renderActions(qm)}</TableCell>
                    </TableRow>
                  ))}
                  {loading && <TableRowSkeleton columns={4} />}
                </TableBody>
              </Table>
            )
          )}
        </div>

        {quickMessages.length > 0 && (
          <div className={classes.footer}>
            {`Mostrando ${quickMessages.length}${totalCount !== null ? ` de ${totalCount}` : ""}`}
            {!loading && !hasMore ? " · todas carregadas" : ""}
          </div>
        )}
      </Paper>
    </MainContainer>
  );
};

export default QuickMessages;
